import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';

/**
 * EXCESSD logo (wordmark): black text, red "D"
 * Compact viewBox so it renders crisply in the header.
 */
const ExcessDLogo = ({ className = "h-8 md:h-10 lg:h-12 w-auto" }) => (
  <svg
    className={className}
    role="img"
    aria-label="EXCESSD logo"
    viewBox="0 0 200 40"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid meet"
  >
    <text
      x="0"
      y="28"
      fontWeight="700"
      fontSize="28"
      fontFamily="'Gill Sans MT','Gill Sans','Segoe UI',Arial,sans-serif"
      fill="#000000"
    >
      EXCESS<tspan fill="#d40000">D</tspan>
    </text>
  </svg>
);

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { setContactFormOpen } = useAppContext();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isCompanyActive = () => location.pathname.startsWith('/company');
  const isCareersActive = () => location.pathname.startsWith('/careers');

  const activeTxt = 'text-[#d40000] border-b-2 border-[#d40000]';
  const hoverTxt = 'hover:text-[#d40000] hover:border-b-2 hover:border-[#d40000]';

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/company/about-us" className="flex items-center" aria-label="Go to About Us">
              <ExcessDLogo />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {/* Company Dropdown */}
            <div className="relative group">
              <button
                className={`px-4 py-2 text-sm font-medium uppercase tracking-wide flex items-center ${
                  isCompanyActive() ? activeTxt : `text-gray-700 ${hoverTxt}`
                }`}
              >
                About Us
                <ChevronDown size={16} className="ml-1" />
              </button>
              <div className="absolute top-full left-0 mt-1 w-56 bg-white shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  {/* <Link
                    to="/company/about-us"
                    className={`block px-4 py-3 text-sm transition-colors rounded ${
                      isActive('/company/about-us') ? 'text-[#d40000] bg-red-50' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    About Us
                  </Link> */}
                  <Link
                    to="/company/our-evolution"
                    className={`block px-4 py-3 text-sm transition-colors rounded ${
                      isActive('/company/our-evolution') ? 'text-[#d40000] bg-red-50' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Our Evolution
                  </Link>
                  {/* <Link
                    to="/company/what-we-offer"
                    className={`block px-4 py-3 text-sm transition-colors rounded ${
                      isActive('/company/what-we-offer') ? 'text-[#d40000] bg-red-50' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    What We Offer
                  </Link> */}
                  {/* <Link
                    to="/company/timeline"
                    className={`block px-4 py-3 text-sm transition-colors rounded ${
                      isActive('/company/timeline') ? 'text-[#d40000] bg-red-50' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Timeline
                  </Link> */}
                </div>
              </div>
            </div>

            {/* Engineering */}
            <Link
              to="/engineering"
              className={`px-4 py-2 text-sm font-medium uppercase tracking-wide ${
                isActive('/engineering') ? activeTxt : `text-gray-700 ${hoverTxt}`
              }`}
            >
              Engineering
            </Link>

            {/* Our Services */}
            <Link
              to="/our-services"
              className={`px-4 py-2 text-sm font-medium uppercase tracking-wide ${
                isActive('/our-services') ? activeTxt : `text-gray-700 ${hoverTxt}`
              }`}
            >
              Our Services
            </Link>

            {/* Gateway to India */}
            <Link
              to="/gateway-to-india"
              className={`px-4 py-2 text-sm font-medium uppercase tracking-wide ${
                isActive('/gateway-to-india') ? activeTxt : `text-gray-700 ${hoverTxt}`
              }`}
            >
              Gateway to India
            </Link>

            {/* Careers Dropdown */}
            <div className="relative group">
              <button
                className={`px-4 py-2 text-sm font-medium uppercase tracking-wide flex items-center ${
                  isCareersActive() ? activeTxt : `text-gray-700 ${hoverTxt}`
                }`}
              >
                Careers
                <ChevronDown size={16} className="ml-1" />
              </button>
              <div className="absolute top-full left-0 mt-1 w-64 bg-white shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <Link
                    to="/careers/working-in-excessd"
                    className={`block px-4 py-3 text-sm transition-colors rounded ${
                      isActive('/careers/working-in-excessd') ? 'text-[#d40000] bg-red-50' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Working in EXCESSD
                  </Link>
                  <Link
                    to="/careers/open-positions"
                    className={`block px-4 py-3 text-sm transition-colors rounded ${
                      isActive('/careers/open-positions') ? 'text-[#d40000] bg-red-50' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Open Positions
                  </Link>
                  {/* <Link
                    to="/careers/internship-program"
                    className={`block px-4 py-3 text-sm transition-colors rounded ${
                      isActive('/careers/internship-program') ? 'text-[#d40000] bg-red-50' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    EXCESSD Internship Program
                  </Link> */}
                </div>
              </div>
            </div>

            {/* Get in Touch Button — black background, red arrow */}
            <div className="ml-6">
              <button
                onClick={() => setContactFormOpen(true)}
                className="bg-yellow-400 text-black font-bold py-2 px-4 rounded-md hover:bg-yellow-400 transition-colors inline-flex items-center justify-center group text-sm uppercase"
              >
                GET IN TOUCH
                {/* <ArrowRight
                  size={16}
                  className="ml-2 group-hover:translate-x-1 transition-transform text-[#d40000]"
                /> */}
              </button>
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <div className="mr-3">
              {/* Mobile Get in Touch — black bg, red arrow */}
              <button
                onClick={() => setContactFormOpen(true)}
                className="bg-yellow-400 text-black font-bold py-2 px-4 rounded-md hover:bg-yellow-300 transition-colors inline-flex items-center justify-center group text-sm uppercase"
              >
                GET IN TOUCH
                <ArrowRight
                  size={12}
                  className="ml-1 group-hover:translate-x-1 transition-transform text-[#d40000]"
                />
              </button>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-[#d40000] focus:outline-none focus:ring-2 focus:ring-[#d40000]/40"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white shadow-lg border-t">
          <div className="px-4 pt-2 pb-3 space-y-1">
            <div className="border-b border-gray-200 pb-2">
              <div className="font-semibold text-gray-800 px-3 py-2">Company</div>
              <Link
                to="/company/about-us"
                className={`block px-6 py-2 text-sm ${
                  isActive('/company/about-us') ? 'bg-red-50 text-[#d40000]' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                About Us
              </Link>
              <Link
                to="/company/our-evolution"
                className={`block px-6 py-2 text-sm ${
                  isActive('/company/our-evolution') ? 'bg-red-50 text-[#d40000]' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Our Evolution
              </Link>
              <Link
                to="/company/what-we-offer"
                className={`block px-6 py-2 text-sm ${
                  isActive('/company/what-we-offer') ? 'bg-red-50 text-[#d40000]' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                What We Offer
              </Link>
              <Link
                to="/company/timeline"
                className={`block px-6 py-2 text-sm ${
                  isActive('/company/timeline') ? 'bg-red-50 text-[#d40000]' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Timeline
              </Link>
            </div>
            <Link
              to="/engineering"
              className={`block px-3 py-2 rounded-md text-base font-medium w-full text-left uppercase ${
                isActive('/engineering') ? 'bg-red-50 text-[#d40000]' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Engineering
            </Link>
            <Link
              to="/our-services"
              className={`block px-3 py-2 rounded-md text-base font-medium w-full text-left uppercase ${
                isActive('/our-services') ? 'bg-red-50 text-[#d40000]' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Our Services
            </Link>
            <Link
              to="/gateway-to-india"
              className={`block px-3 py-2 rounded-md text-base font-medium w-full text-left uppercase ${
                isActive('/gateway-to-india') ? 'bg-red-50 text-[#d40000]' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Gateway to India
            </Link>
            <div className="border-b border-gray-200 pb-2">
              <div className="font-semibold text-gray-800 px-3 py-2">Careers</div>
              <Link
                to="/careers/working-in-excessd"
                className={`block px-6 py-2 text-sm ${
                  isActive('/careers/working-in-excessd') ? 'bg-red-50 text-[#d40000]' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Working in EXCESSD
              </Link>
              <Link
                to="/careers/open-positions"
                className={`block px-6 py-2 text-sm ${
                  isActive('/careers/open-positions') ? 'bg-red-50 text-[#d40000]' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Open Positions
              </Link>
              <Link
                to="/careers/internship-program"
                className={`block px-6 py-2 text-sm ${
                  isActive('/careers/internship-program') ? 'bg-red-50 text-[#d40000]' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                EXCESSD Internship Program
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
